// src/components/ClientPortalLayout.js

import React, { useState } from 'react';

// Import Client Portal specific content components
import ClientPortalDashboard from './ClientPortalDashboard';
import ClientPortalCases from './ClientPortalCases';
import ClientPortalDocuments from './ClientPortalDocuments';
import ClientPortalInvoices from './ClientPortalInvoices';
import ClientPortalCommunicationLogs from './ClientPortalCommunicationLogs';
import ClientPortalTasks from './ClientPortalTasks';
import ClientPortalTimeExpenses from './ClientPortalTimeExpenses';
import ClientPortalDebtRecovery from './ClientPortalDebtRecovery';

const NAV_ITEMS = [
  { key: 'client-dashboard', label: 'Dashboard', icon: 'bi-house-door' },
  { key: 'client-cases', label: 'Cases', icon: 'bi-folder' },
  { key: 'client-documents', label: 'Documents', icon: 'bi-file-earmark-text' },
  { key: 'client-invoices', label: 'Invoices', icon: 'bi-receipt' },
  { key: 'client-debt-recovery', label: 'Debt Recovery', icon: 'bi-cash-coin' },
  { key: 'client-communication', label: 'Communication', icon: 'bi-chat-dots' },
  { key: 'client-tasks', label: 'Tasks', icon: 'bi-check2-square' },
  { key: 'client-time-expenses', label: 'Time & Expenses', icon: 'bi-clock-history' },
];

function ClientPortalLayout({
  currentUser,
  clients,
  cases,
  documents,
  timeEntries,
  expenseEntries,
  invoices,
  invoiceItems,
  communicationLogs,
  tasks,
  authAxios,
  showFormMessage,
  handleLogout,
  debtRecoveryMandates,
}) {
  const [activeClientTab, setActiveClientTab] = useState('client-dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="flex h-screen bg-gray-100 font-sans">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-primary text-white flex flex-col transform transition-transform duration-200 ease-in-out lg:relative lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Brand */}
        <div className="flex items-center justify-between h-16 px-5 border-b border-white/10 shrink-0">
          <button
            className="text-lg font-bold tracking-wide hover:text-accent transition-colors"
            onClick={() => { setActiveClientTab('client-dashboard'); setSidebarOpen(false); }}
          >
            Client Portal
          </button>
          <button
            className="lg:hidden text-white/70 hover:text-white"
            onClick={() => setSidebarOpen(false)}
          >
            <i className="bi bi-x-lg text-xl"></i>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const isActive = activeClientTab === item.key;
            return (
              <button
                key={item.key}
                onClick={() => { setActiveClientTab(item.key); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-accent text-white shadow-md'
                    : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
              >
                <i className={`bi ${item.icon} text-base`}></i>
                {item.label}
              </button>
            );
          })}
        </nav>

        {/* User section + Logout */}
        {currentUser && (
          <div className="border-t border-white/10 p-4 shrink-0">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-accent flex items-center justify-center text-sm font-bold">
                {currentUser.username?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{currentUser.username}</div>
                <div className="text-xs text-white/50">Client</div>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-error/20 text-red-200 hover:bg-error/40 hover:text-white transition-colors"
            >
              <i className="bi bi-box-arrow-left"></i>
              Logout
            </button>
          </div>
        )}
      </aside>

      {/* Main content area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar (mobile hamburger + page title) */}
        <header className="flex items-center h-16 px-4 bg-white border-b border-gray-200 shrink-0 lg:px-6">
          <button
            className="lg:hidden mr-3 text-gray-600 hover:text-gray-900"
            onClick={() => setSidebarOpen(true)}
          >
            <i className="bi bi-list text-2xl"></i>
          </button>
          <h1 className="text-lg font-semibold text-gray-800">
            {NAV_ITEMS.find((n) => n.key === activeClientTab)?.label || 'Dashboard'}
          </h1>
        </header>

        {/* Scrollable content */}
        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {activeClientTab === 'client-dashboard' && (
            <ClientPortalDashboard
              currentUser={currentUser}
              clients={clients}
              cases={cases}
              documents={documents}
              invoices={invoices}
              tasks={tasks}
              communicationLogs={communicationLogs}
            />
          )}
          {activeClientTab === 'client-cases' && (
            <ClientPortalCases
              currentUser={currentUser}
              clients={clients}
              cases={cases}
            />
          )}
          {activeClientTab === 'client-documents' && (
            <ClientPortalDocuments
              currentUser={currentUser}
              clients={clients}
              documents={documents}
              cases={cases}
              authAxios={authAxios}
              showFormMessage={showFormMessage}
            />
          )}
          {activeClientTab === 'client-invoices' && (
            <ClientPortalInvoices
              currentUser={currentUser}
              clients={clients}
              invoices={invoices}
              invoiceItems={invoiceItems}
              authAxios={authAxios}
            />
          )}
          {activeClientTab === 'client-debt-recovery' && (
            <ClientPortalDebtRecovery
              currentUser={currentUser}
              clients={clients}
              mandates={debtRecoveryMandates || []}
              communicationLogs={communicationLogs}
              authAxios={authAxios}
            />
          )}
          {activeClientTab === 'client-communication' && (
            <ClientPortalCommunicationLogs
              currentUser={currentUser}
              clients={clients}
              communicationLogs={communicationLogs}
              cases={cases}
            />
          )}
          {activeClientTab === 'client-tasks' && (
            <ClientPortalTasks
              currentUser={currentUser}
              clients={clients}
              tasks={tasks}
              cases={cases}
            />
          )}
          {activeClientTab === 'client-time-expenses' && (
            <ClientPortalTimeExpenses
              currentUser={currentUser}
              clients={clients}
              timeEntries={timeEntries}
              expenseEntries={expenseEntries}
              cases={cases}
            />
          )}
        </main>
      </div>
    </div>
  );
}

export default ClientPortalLayout;
